<template>
  <div>
    <Card class="tw-p-8 tw-mb-4">
      <div class="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-x-8 tw-gap-y-4 tw-mb-4">
        <!-- Bank -->
        <div>
          <Label>
            {{ t('payments.sepa.choose_bank') }}
          </Label>
          <BankAccountSelect
            ref="bankSelectRef"
            v-model="formData.bank"
            :customer-id="formData.customer"
            :placeholder="t('payments.sepa.select_bank_placeholder')"
          />
        </div>
      </div>
    </Card>
    <div v-if="formData.customer === null" class="tw-text-center">
      {{ t('sepa.titles.please_select_customer') }}
    </div>
    <div v-else>
      <Card class="tw-p-8 tw-mb-4">
        <div
          class="tw-text-base tw-font-medium tw-mb-4 tw-uppercase tw-text-[#3f4456] tw-pb-4 tw-border-b tw-border-[#d7ddf5]"
        >
          {{ t('payments.sepa.bank_details') }}
        </div>
        <!-- Account Owner and Swift Code -->
        <div class="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-x-8 tw-gap-y-4 tw-mb-4">
          <div>
            <Label>
              {{ t('sepa_payments.titles.account_owner') }}
            </Label>
            <Input
              type="text"
              :placeholder="t('payments.sepa.enter_account_owner')"
              @keyup="changeValuekeyUp()"
              v-model="sepaPayment.owner"
            />
          </div>
          <div>
            <Label>
              {{ t('sepa_payments.titles.swift_code_bic') }}
            </Label>
            <Input
              type="text"
              :placeholder="t('payments.sepa.enter_swift_code_bic')"
              @keyup="changeValuekeyUp()"
              v-model="sepaPayment.swiftCode"
            />
          </div>
        </div>
        <!-- IBAN and Execution Date -->
        <div class="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-x-8 tw-gap-y-4 tw-mb-4">
          <div>
            <Label>
              {{ t('sepa_payments.titles.iban') }}
            </Label>
            <Input
              type="text"
              :placeholder="t('payments.sepa.enter_iban')"
              @keyup="changeValuekeyUp()"
              v-model="sepaPayment.iban"
            />
          </div>
          <div>
            <Label>
              {{ t('sepa_payments.titles.execution_date') }}
            </Label>
            <DatePicker
              v-model="sepaPayment.execution_date"
              :placeholder="t('payments.sepa.select_date')"
            />
          </div>
        </div>
      </Card>

      <Card class="tw-p-8 tw-mb-4">
        <div
          class="tw-text-base tw-font-medium tw-mb-4 tw-uppercase tw-text-[#3f4456] tw-pb-4 tw-border-b tw-border-[#d7ddf5]"
        >
          {{ t('payments.sepa.select_payments') }}
        </div>
        <SelectPaymentsTable
          :customer="formData.customer"
          @select-payments="updateSelectedPayments"
        />
      </Card>

      <!-- Actions -->
      <div>
        <div v-if="isCreatedSuccessfully != null" class="tw-mb-4">
          <Alert v-if="isCreatedSuccessfully" class="md:tw-w-5/12">
            <AlertDescription>{{ t('payment.titles.sepa_create_success') }}</AlertDescription>
          </Alert>
          <Alert v-else variant="destructive" class="md:tw-w-5/12">
            <AlertDescription>{{ errorMessage }}</AlertDescription>
          </Alert>
        </div>

        <div class="tw-flex tw-gap-2 tw-justify-end">
          <CustomerActionButton
            class="tw-bg-primary-500 tw-text-white tw-px-4 tw-py-2 tw-rounded-md tw-text-sm tw-flex tw-gap-2"
            :disabled="isValid"
            :style="isValid ? 'cursor: not-allowed' : ''"
            @click="generate()"
          >
            {{ t('payments.sepa.generate') }}
          </CustomerActionButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import BankAccountSelect from '@/components/shared/BankAccountSelect.vue';
import SelectPaymentsTable from '@/components/shared/tables/sepa-payments-table/SelectPaymentsTable.vue';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { ref, reactive, onMounted, type Ref, watch, computed } from 'vue';

import moment from 'moment';
import { DatePicker } from '@/components/ui/date-picker';
import router from '@/router';

import { useBankAccountStore } from '@/stores/bank-account.store';
import { useSepaStore } from '@/stores/sepa.store';
import { useI18n } from 'vue-i18n';
import CustomerActionButton from '@/components/shared/CustomerActionButton.vue';
import { useBreadcrumbs } from '@/composables/useBreadcrumbs';
import cookieService from '@/_helpers/services/cookie.service';

const { t } = useI18n();
const { setBreadcrumbs } = useBreadcrumbs();
const { getBankAccountById, createBankAccount } = useBankAccountStore();
const { createSepaPayment } = useSepaStore();

let formData: any = reactive({
  customer: null,
  project: null,
  bank: null,
});

const isCreatedSuccessfully: Ref<boolean | null> = ref(null);

const rules = {
  customer: {
    required,
  },
};

const bankSelectRef = ref<any>(null);

const errorMessage: any = ref('');

const keyUp: any = ref(false);

const $v = useVuelidate(rules, formData);

let sepaPayment: any = ref({
  owner: '',
  swiftCode: '',
  iban: '',
  execution_date: new Date(),
});

let paymentData: any = ref([]);

/* Get Bank details*/
const getBankDetail = (bank_id: number) => {
  getBankAccountById(bank_id)
    .then(response => {
      sepaPayment.value.owner = response.owner_name;
      sepaPayment.value.swiftCode = response.swift_code;
      sepaPayment.value.iban = response.iban;
      keyUp.value = false;
    })
    .catch(error => console.log(error));
};

const formatDate = (date: Date) => {
  return moment(date).format('DD.MM.YYYY');
};

const updateSelectedPayments = (newValue: any) => {
  paymentData.value = [];
  newValue.map((payment: any) => {
    let obj = {
      payment_id: payment.id,
      note: payment.invoice_number !== null ? payment.invoice_number : '',
      payment_amount_type: payment.amount_to_use,
    };
    paymentData.value.push(obj);
  });
};

const changeValuekeyUp = () => {
  keyUp.value = true;
};
const selectedBank = computed(() => {
  const list = Array.isArray(bankSelectRef.value?.bankAccounts)
    ? bankSelectRef.value.bankAccounts
    : bankSelectRef.value?.bankAccounts?.value || [];
  return list.find((b: any) => b.id === formData.bank);
});

const generateBankDetail = async () => {
  await createBankAccount({
    owner_name: sepaPayment.value.owner,
    bank_name: selectedBank.value?.bank_name,
    swift_code: sepaPayment.value.swiftCode,
    iban: sepaPayment.value.iban,
    customer: formData.customer !== null ? formData.customer : null,
  });
};

const createSepa = async () => {
  let payload = {
    customer: formData.customer,
    payment_data: paymentData.value,
    source_account: formData.bank,
    execution_date: moment(sepaPayment.value.execution_date).format('YYYY-MM-DD'),
  };

  await createSepaPayment(payload)
    .then(() => {
      isCreatedSuccessfully.value = true;
      setTimeout(() => {
        router.push({ name: 'sepa-payments' });
      }, 2000);
    })
    .catch(error => {
      console.log(error);
      isCreatedSuccessfully.value = false;
      errorMessage.value = error.response.data.errors[0].detail;
    });
};

const isValid = computed(() => {
  return (
    sepaPayment.value.owner === '' ||
    sepaPayment.value.swiftCode === '' ||
    sepaPayment.value.iban === ''
  );
});

const generate = async () => {
  isCreatedSuccessfully.value = null;
  if (keyUp.value === true) {
    await generateBankDetail()
      .then(() => {
        createSepa();
      })
      .catch(error => {
        isCreatedSuccessfully.value = false;
        if (error.response.data.type === 'validation_error') {
          let field = error.response.data.errors[0].attr.replace('_', ' ');
          field = field.toUpperCase();
          errorMessage.value = field + ': ' + error.response.data.errors[0].detail;
        } else {
          errorMessage.value = error.response.data.errors[0].detail;
        }
      });
  } else {
    createSepa();
  }
};

onMounted(async () => {
  setBreadcrumbs([
    {
      labelKey: 'sidebar.items.sepa',
      to: { name: 'sepa-payments' },
    },
    {
      labelKey: 'sepa_payments.titles.new_sepa_payment',
    },
  ]);

  let getSelectedCustomerID = cookieService.get('selectedCustomerId');
  if (getSelectedCustomerID) {
    formData.customer = JSON.parse(getSelectedCustomerID);
  }
});

watch(
  () => formData.bank,
  async newVal => {
    if (newVal) {
      await getBankDetail(newVal);
    }
  }
);
</script>
